import { useEffect, useState } from "react";
import { Checkbox, InputNumber, Radio } from "antd";
import { CompanyService } from "../../Services/CompanyService";
import { getCurrentLanguage } from "../../Helpers/RouteHelper";
import { TextService } from "../../Services/TextService";
import { TimeChoice } from "../../Types/TimeChoice";
import { InfoTooltip } from "./InfoTooltip";

type EventSignUpSettingsProps = {
    membersOnly: boolean,
    setMembersOnly: (value: boolean) => void,
    showOnMemberSite: boolean,
    setShowOnMemberSite: (value: boolean) => void,
    timeChoice: TimeChoice,
    setTimeChoice: (value: TimeChoice) => void,
    /** A poll has no limit on the number of times a participant chooses. */
    isPoll: boolean,
    /** The number of times the admin has added to the event. */
    timesCount: number
}

/**
 * The settings for who can sign up to an event and how: members only, shown on the member site and how
 * many times a participant may choose. One setting per row, each with its explanation in an info symbol.
 * The waitlist sits with the max number of places (EventWaitlistSettings).
 */
export const EventSignUpSettings = (props: EventSignUpSettingsProps) => {
    const GetText = TextService.GetText;
    const [hasPublicSite, setHasPublicSite] = useState(true);

    // Members log in through the company's public site, so two of the settings need one.
    useEffect(() => {
        CompanyService.GetCompanyPublicSite().then(result => {
            setHasPublicSite(!!result?.siteUrl);
        }).catch(() => {
            setHasPublicSite(false);
        });
    }, []);

    const rowStyle = { marginBottom: "6px" };

    return <div style={{ textAlign: "left" }}>
        <div style={rowStyle}>
            {/* A setting that already is on can always be turned off again. */}
            <Checkbox checked={props.membersOnly} disabled={!hasPublicSite && !props.membersOnly} onChange={(e) => props.setMembersOnly(e.target.checked)}>
                {GetText("MembersOnly_Checkbox")}
            </Checkbox>
            <InfoTooltip text={GetText("MembersOnly_Help")} />
        </div>

        <div style={rowStyle}>
            <Checkbox checked={props.showOnMemberSite} disabled={!hasPublicSite && !props.showOnMemberSite} onChange={(e) => props.setShowOnMemberSite(e.target.checked)}>
                {GetText("ShowOnMemberSite_Checkbox")}
            </Checkbox>
            <InfoTooltip text={GetText("ShowOnMemberSite_Help")} />
        </div>

        {/* The link uses the names of the menu itself, so the path can't drift from what the admin sees. */}
        {!hasPublicSite ? <div style={{ ...rowStyle, fontSize: "80%" }}>
            <i>{GetText("MembersOnly_RequiresPublicSite")} <a href={"/" + getCurrentLanguage() + "/publicsite"} target="_blank" rel="noreferrer">{GetText("TopHeader_Settings")} &gt; {GetText("PublicSiteForm_Title")}</a>.</i>
        </div> : null}

        {/* Only a question when there are several times to choose between. */}
        {!props.isPoll && props.timesCount > 1 ? <div style={rowStyle}>
            {GetText("TimeChoice_Label")}
            <Radio.Group value={props.timeChoice} onChange={(e) => props.setTimeChoice(e.target.value)} style={{ marginLeft: "8px" }}>
                <Radio value={TimeChoice.Several}>{GetText("TimeChoice_Several")}</Radio>
                <Radio value={TimeChoice.One}>{GetText("TimeChoice_One")}</Radio>
            </Radio.Group>
            <InfoTooltip text={GetText("TimeChoice_Help")} />
        </div> : null}
    </div>;
};

/** Allow a waitlist when the places are taken, and how many may stand on it. Only for a sign-up. */
export const EventWaitlistSettings = (props: {
    allowWaitlist: boolean,
    setAllowWaitlist: (value: boolean) => void,
    maxWaitlist: number | null,
    setMaxWaitlist: (value: number | null) => void,
}) => {
    const GetText = TextService.GetText;
    const rowStyle = { marginTop: "6px" };

    return <div style={{ textAlign: "left" }}>
        <div style={rowStyle}>
            <Checkbox checked={props.allowWaitlist} onChange={(e) => props.setAllowWaitlist(e.target.checked)}>
                {GetText("Waitlist_Allow_Checkbox")}
            </Checkbox>
            <InfoTooltip text={GetText("Waitlist_Allow_Help")} />
        </div>

        {props.allowWaitlist ?
            <div style={rowStyle}>
                {/* Box first and text to the right, lined up with the max number of places. */}
                <InputNumber min={1} max={9999} value={props.maxWaitlist} onChange={(value) => props.setMaxWaitlist(value)} style={{ width: "90px" }} />
                <span style={{ marginLeft: "8px" }}>{GetText("Waitlist_Max_Label")}</span>
                <InfoTooltip text={GetText("Waitlist_Max_Help")} />
            </div> : null}
    </div>;
};
